import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaBriefcase,
    FaUsers,
    FaCalendarCheck,
    FaArrowUp,
    FaArrowDown,
    FaPlus,
    FaEllipsisV,
    FaRegBell,
    FaUserGraduate,
    FaArrowRight,
    FaMapMarkerAlt
} from 'react-icons/fa';
import WaveChart from '../../SuperAdmin/shared/WaveChart';
import DataTable from '../../SuperAdmin/shared/DataTable';
import Loader from '../../shared/Loader';
import './HiringHub.css';

const HiringHub = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        activeJobs: 0,
        totalApplicants: 0,
        upcomingInterviews: 0
    });
    const [jobs, setJobs] = useState([]);
    const [interviews, setInterviews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;

                const [statsRes, jobsRes, interviewsRes] = await Promise.all([
                    axios.get('/api/recruiter/dashboard-stats', { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get('/api/recruiter/jobs', { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get('/api/recruiter/interviews', { headers: { Authorization: `Bearer ${token}` } })
                ]);

                setStats(statsRes.data);
                setJobs(jobsRes.data);
                setInterviews(interviewsRes.data);
            } catch (err) {
                console.error("Error fetching dashboard data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    const chartData = [
        { name: 'Mon', apps: 4 },
        { name: 'Tue', apps: 7 },
        { name: 'Wed', apps: 5 },
        { name: 'Thu', apps: 12 },
        { name: 'Fri', apps: 9 },
        { name: 'Sat', apps: 3 },
        { name: 'Sun', apps: 6 },
    ];

    const jobColumns = [
        {
            header: 'Job Title',
            key: 'title',
            render: (row) => (
                <span className="job-title-text" style={{ fontWeight: 700, color: '#1e3a8a' }}>{row.title}</span>
            )
        },
        {
            header: 'Location',
            key: 'location',
            render: (row) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '13px' }}>
                    <FaMapMarkerAlt style={{ color: '#1D4ED8', flexShrink: 0 }} />
                    {row.location || '—'}
                </span>
            )
        },
        {
            header: 'Status',
            key: 'status',
            render: (row) => (
                <span
                    className={`status-badge ${row.status.toLowerCase()}`}
                    style={{ cursor: 'pointer' }}
                >
                    {row.status}
                </span>
            )
        },
        { header: 'Applicants', key: 'applicantCount' },
        {
            header: 'Posted On',
            key: 'postedAt',
            render: (row) => new Date(row.postedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        }
    ];

    if (loading) return <Loader />;

    return (
        <div className="dashboard-overview fade-in">
            <div className="dashboard-header">
                <div>
                    <h1 className="page-title">Hiring Hub Overview</h1>
                    <p className="page-subtitle">Track your recruitment pipeline and candidate engagement.</p>
                </div>
            </div>

            {/* KPI Section */}
            <div className="kpi-grid">
                <div className="kpi-card" onClick={() => navigate('/recruiter/jobs')} style={{ cursor: 'pointer' }}>
                    <div className="kpi-header">
                        <span className="kpi-title">Active Job Posts</span>
                        <div className="kpi-icon-wrapper"><FaBriefcase /></div>
                    </div>
                    <h2 className="kpi-value">{stats.activeJobs}</h2>
                    <div className="kpi-trend trend-up">
                        <FaArrowUp /> 12% <span className="trend-label">vs last month</span>
                    </div>
                </div>

                <div className="kpi-card" onClick={() => navigate('/recruiter/applicants')} style={{ cursor: 'pointer' }}>
                    <div className="kpi-header">
                        <span className="kpi-title">Total Applicants</span>
                        <div className="kpi-icon-wrapper"><FaUsers /></div>
                    </div>
                    <h2 className="kpi-value">{stats.totalApplicants}</h2>
                    <div className="kpi-trend trend-up">
                        <FaArrowUp /> 24% <span className="trend-label">vs last month</span>
                    </div>
                </div>

                <div className="kpi-card" onClick={() => navigate('/recruiter/interviews')} style={{ cursor: 'pointer' }}>
                    <div className="kpi-header">
                        <span className="kpi-title">Interviews Scheduled</span>
                        <div className="kpi-icon-wrapper"><FaCalendarCheck /></div>
                    </div>
                    <h2 className="kpi-value">{stats.upcomingInterviews}</h2>
                    <div className="kpi-trend trend-down">
                        <FaArrowDown /> 4% <span className="trend-label">vs last month</span>
                    </div>
                </div>

                <div className="kpi-card" onClick={() => navigate('/recruiter/reports')} style={{ cursor: 'pointer' }}>
                    <div className="kpi-header">
                        <span className="kpi-title">Candidate Match Rate</span>
                        <div className="kpi-icon-wrapper"><FaUserGraduate /></div>
                    </div>
                    <h2 className="kpi-value">86%</h2>
                    <div className="kpi-trend trend-up">
                        <FaArrowUp /> 5% <span className="trend-label">efficiency gain</span>
                    </div>
                </div>
            </div>

            {/* Charts Section */}
            <div className="charts-grid">
                <div className="chart-card">
                    <div className="section-title">
                        <span>Applicant Inflow</span>
                    </div>
                    <WaveChart data={chartData} xKey="name" yKey="apps" color="#D4AF37" />
                </div>

                <div className="chart-card">
                    <div className="section-title">
                        <span>Upcoming Interviews</span>
                    </div>
                    <div className="interview-list">
                        {interviews.length > 0 ? interviews.slice(0, 4).map((interview) => (
                            <div key={interview._id} className="interview-item">
                                <div className="interview-date">
                                    <span className="day">{new Date(interview.scheduledDate).getDate()}</span>
                                    <span className="month">{new Date(interview.scheduledDate).toLocaleString('default', { month: 'short' })}</span>
                                </div>
                                <div className="interview-info">
                                    <h4>{interview.candidateId?.name}</h4>
                                    <p>{interview.jobId?.title} • {interview.timeSlot}</p>
                                </div>
                                <div className="interview-action">
                                    <FaPlus className="action-icon" />
                                </div>
                            </div>
                        )) : (
                            <div className="empty-state">
                                <FaCalendarCheck size={32} />
                                <p>No scheduled interviews</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Jobs Table Section */}
            <div className="premium-card overview-jobs-card">
                <div className="card-header-with-action">
                    <h3 className="section-title" onClick={() => navigate('/recruiter/jobs')} style={{ cursor: 'pointer' }}>Recent Active Listings</h3>
                    <button className="secondary-btn flex-center" onClick={() => navigate('/recruiter/jobs')}>
                        View All <FaArrowRight style={{ marginLeft: '8px' }} />
                    </button>
                </div>
                <DataTable
                    columns={jobColumns}
                    data={jobs.filter(j => j.status === 'Active').slice(0, 5)}
                    hidePagination={true}
                    onRowClick={() => navigate('/recruiter/jobs')}
                />
            </div>
        </div>
    );
};

export default HiringHub;
